import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { PatientAuthProvider } from "@/contexts/PatientAuthContext";
import { Suspense, lazy } from "react";
import { LoadingSpinner } from "@/components/ui/spinner";
import { CookieConsent } from "@/components/CookieConsent";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import SupportWidget from "@/components/SupportWidget";

const Landing = lazy(() => import("./pages/Landing"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CleanupTemplates = lazy(() => import("./pages/CleanupTemplates"));
const Auth = lazy(() => import("./pages/Auth"));
const Patients = lazy(() => import("./pages/Patients"));
const PatientDetail = lazy(() => import("./pages/PatientDetail"));
const Schedule = lazy(() => import("./pages/Schedule"));
const Finances = lazy(() => import("./pages/Finances"));
const Settings = lazy(() => import("./pages/Settings"));
const SignDocument = lazy(() => import("./pages/SignDocument"));
const PaymentPlans = lazy(() => import("./pages/PaymentPlans"));
const Templates = lazy(() => import("./pages/Templates"));
const FormBuilder = lazy(() => import("./pages/FormBuilder"));
const Subscription = lazy(() => import("./pages/Subscription"));
const Guide = lazy(() => import("./pages/Guide"));
const PatientPortal = lazy(() => import("./pages/PatientPortal"));
const PatientPortalAuth = lazy(() => import("./pages/PatientPortalAuth"));
const MyAppointments = lazy(() => import("./pages/MyAppointments"));
const PasswordReset = lazy(() => import("./pages/PasswordReset"));
const HealthInsurance = lazy(() => import("./pages/HealthInsurance"));
const Profile = lazy(() => import("./pages/Profile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <InstallPrompt />
        <AuthProvider>
          <PatientAuthProvider>
            {/* <SupportWidget /> */}
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* Public routes */}
                <Route
                  path="/"
                  element={
                    <>
                      <Landing />
                      <CookieConsent />
                    </>
                  }
                />
                <Route path="/privacidade" element={<PrivacyPolicy />} />
                <Route path="/termos" element={<TermsOfUse />} />
                <Route path="/auth" element={<Auth />} />

                {/* Psychologist routes */}
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/cleanup" element={<CleanupTemplates />} />
                <Route path="/patients" element={<Patients />} />
                <Route path="/patients/:id" element={<PatientDetail />} />
                <Route path="/schedule" element={<Schedule />} />
                <Route path="/finances" element={<Finances />} />
                <Route path="/plans" element={<PaymentPlans />} />
                <Route path="/templates" element={<Templates />} />
                <Route path="/forms/new" element={<FormBuilder />} />
                <Route path="/forms/:id/edit" element={<FormBuilder />} />
                <Route path="/subscription" element={<Subscription />} />
                <Route path="/guide" element={<Guide />} />
                <Route path="/convenios" element={<HealthInsurance />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/assinatura/:guideId" element={<SignDocument />} />

                {/* Patient portal routes */}
                <Route path="/portal/login" element={<PatientPortalAuth />} />
                <Route path="/portal" element={<PatientPortal />} />
                <Route
                  path="/portal/appointments"
                  element={<MyAppointments />}
                />
                <Route
                  path="/portal/reset-password"
                  element={<PasswordReset />}
                />
                <Route path="/reset-password" element={<PasswordReset />} />
                <Route
                  path="/definir-senha-paciente"
                  element={<PasswordReset />}
                />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </PatientAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    <SpeedInsights />
  </QueryClientProvider>
);

export default App;
